import fuelTranslate from './fuelTranslate'
import gearboxTranslate from './gearboxTranslate'
import { BODY_TYPE_MAPPINGS, getCommonBodyTypes } from './strictValidationMappings'

// Body type enum translations for database values
const bodyTypeTranslate: Record<string, string> = {
  'BODY.AUTOMOBILE.SEDAN': 'Sedán',
  'BODY.AUTOMOBILE.SUV': 'SUV',
  'BODY.AUTOMOBILE.PICK_UP': 'Pickup',
  'BODY.AUTOMOBILE.HATCHBACK': 'Hatchback',
  'BODY.AUTOMOBILE.COUPE': 'Coupé',
  'BODY.AUTOMOBILE.VAN': 'Van',
  'BODY.AUTOMOBILE.SWAGON': 'Station Wagon',
  'BODY.AUTOMOBILE.CITYCAR': 'Citycar',
  'BODY.AUTOMOBILE.ALL_TERRAIN': 'Todo Terreno',
  'BODY.AUTOMOBILE.COCKPIT': 'Cockpit',
  'BODY.AUTOMOBILE.OTHER': 'Otro',
  'BODY.MOTORCYCLE.STREET': 'Moto de Calle',
  'BODY.MOTORCYCLE.TRAIL': 'Moto Trail',
  'BODY.MOTORCYCLE.SCOOTER': 'Scooter',
  'BODY.MOTORCYCLE.CROSS': 'Moto Cross',
  'BODY.MOTORCYCLE.SIMPLE': 'Moto Simple',
  'BODY.MOTORCYCLE.CUSTOM': 'Moto Custom',
  'BODY.MOTORCYCLE.SPORT': 'Moto Deportiva',
  'BODY.MOTORCYCLE.SNOW': 'Moto de Nieve',
  'BODY.MOTORCYCLE.TRICYCLE': 'Triciclo',
  'BODY.MOTORCYCLE.OTHER': 'Otra Moto',
}

/**
 * Translates vehicle fuel type from database value to display value
 * @param fuel - Raw fuel value from database (e.g., 'FUEL.UNLEADED')
 * @returns User-friendly fuel name (e.g., 'Bencina')
 */
export const translateFuel = (fuel: string | undefined): string => {
  if (!fuel) return ''

  return fuelTranslate[fuel] || fuel
}

/**
 * Translates vehicle gearbox type from database value to display value
 * @param gearbox - Raw gearbox value from database (e.g., 'GEARBOX.AUTO')
 * @returns User-friendly gearbox name (e.g., 'Automática')
 */
export const translateGearbox = (gearbox: string | undefined): string => {
  if (!gearbox) return ''

  return gearboxTranslate[gearbox] || gearbox
}

/**
 * Translates vehicle body type from database value to display value
 * @param body - Raw body value from database (e.g., 'BODY.AUTOMOBILE.SUV')
 * @returns User-friendly body name (e.g., 'SUV')
 */
export const translateBody = (body: string | undefined): string => {
  if (!body) return ''

  // First check if it's a database enum value (contains 'BODY.')
  if (body.includes('BODY.')) {
    return bodyTypeTranslate[body] || body
  }

  // Then try to find in mappings by common value
  const mapping = BODY_TYPE_MAPPINGS.find(
    (m) => m.common === body || m.chileautos === body || m.mercadolibre === body || m.yapo === body
  )

  return mapping?.common || body
}

/**
 * Gets the formatted display value, with fallback to translated raw value
 * @param formatValue - Formatted value from database (*_format field)
 * @param rawValue - Raw value from database
 * @param translateFn - Translation function for the raw value
 * @returns Best available display value
 */
export const getDisplayValue = (
  formatValue: string | undefined,
  rawValue: string | undefined,
  translateFn: (value: string | undefined) => string
): string => {
  // Prefer formatted value if available and not empty
  if (formatValue && formatValue.trim()) return formatValue

  // Otherwise translate raw value
  return translateFn(rawValue)
}

/**
 * Gets all available body type display values for dropdown options
 * Combines both mapped common types and all translated database enum types
 * @returns Array of all unique body type display values
 */
export const getAllBodyTypes = (): string[] => {
  const allBodyTypes = new Set<string>()

  // Add all translated database enum values
  Object.values(bodyTypeTranslate).forEach((displayValue) => {
    allBodyTypes.add(displayValue)
  })

  // Add all common body types from mappings (for platform consistency)
  const commonTypes = getCommonBodyTypes()
  commonTypes.forEach((mapping) => {
    allBodyTypes.add(mapping.common)
  })

  // Return sorted array for consistent dropdown order
  return Array.from(allBodyTypes).sort()
}
